import React, { useCallback, useEffect, useState } from 'react';
import { Paper, Typography } from '@mui/material';
import sentinelNetwork from '@/lib/api/sentinelNetwork';
import { getToolReportDetail } from '@/lib/api/toolReportService';
import PolarisSection, { type PolarisData } from './PolarisSection';

const EMPTY_COUNTS = { critical: 0, high: 0, medium: 0, low: 0, unknown: 0 };

// ============================================================
// Polaris Repo Tab — renders the PolarisSection scoped to a single
// repository inside the repo card's detail tabs. The backend slices
// rawReport.projects[] to the matching Polaris project; when no
// Polaris project name matches this repo we show a mapping hint.
// ============================================================
function PolarisRepoTab({ appId, repoSlug }: { appId: string; repoSlug: string }) {
  const [loading, setLoading] = useState(false);
  const [configured, setConfigured] = useState(true);
  const [data, setData] = useState<PolarisData | null>(null);
  const [storedAt, setStoredAt] = useState<string | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [noMapping, setNoMapping] = useState(false);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setNoMapping(false);
    try {
      const configRes = await sentinelNetwork.get(`/config/project/${appId}/polaris`);
      if (!configRes.data?.data?.applicationId) {
        setConfigured(false);
        setData(null);
        return;
      }
      setConfigured(true);

      const detail = await getToolReportDetail(appId, 'polaris', repoSlug);
      const raw = detail.rawReport as (PolarisData & { projects?: any[] }) | null;
      const project = raw?.projects?.[0];

      if (!project) {
        // Either Polaris is configured but no project name matches this repo,
        // or the report predates per-project fetching / used the fallback path.
        setNoMapping(!!raw);
        setData(null);
      } else {
        setData({
          applicationId: raw?.applicationId ?? '',
          applicationName: raw?.applicationName ?? project.projectName ?? null,
          applicationUrl: raw?.applicationUrl ?? null,
          fetchedAt: raw?.fetchedAt ?? '',
          totalCount: project.totalCount ?? project.findings?.length ?? 0,
          truncated: !!project.truncated,
          severityCounts: project.severityCounts ?? { ...EMPTY_COUNTS },
          findings: project.findings ?? [],
          error: project.error,
        });
      }
      setStoredAt(detail.analyzedAt ?? null);
      setFetchError(null);
    } catch (err: any) {
      console.warn('[Polaris] repo-scoped fetch failed:', err?.message);
      setFetchError(err?.response?.data?.message || err?.message || 'Failed to load Polaris report');
    } finally {
      setLoading(false);
    }
  }, [appId, repoSlug]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  if (noMapping) {
    return (
      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
        <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
          No Polaris project is mapped to this repository.
        </Typography>
        <Typography sx={{ fontSize: 11, color: 'text.disabled', mt: 0.5 }}>
          Configure a Polaris project name matching this repository slug in Project Settings → Connections.
        </Typography>
      </Paper>
    );
  }

  return (
    <PolarisSection
      loading={loading}
      data={data}
      configured={configured}
      storedAt={storedAt}
      fetchError={fetchError}
      onRetry={fetchReport}
      emptyMessage="No open Polaris findings for this repository."
    />
  );
}

export default PolarisRepoTab;
